// Die drei Zustaende, die jede Liste dieser Anwendung hat, bevor sie Daten hat. Sie liegen in einer
// Datei, weil sie zusammen gelesen werden muessen:
//
//   * Ein Ladezustand, der die Spalten anders aufteilt als die fertige Tabelle, verschiebt beim
//     Eintreffen der Daten das ganze Layout. Deshalb rendert TabellenZustand im Ladefall exakt so
//     viele Zeilen mal Spalten, wie die Tabelle danach hat — die Spaltenbreiten stehen also schon.
//   * Ein leerer Zustand, der nur "keine Daten" sagt, laesst den Benutzer ratlos zurueck. Hier steht
//     immer, WAS hier stuende und WIE es hierher kommt.
//   * Ein Fehlerzustand, der die deutsche Servermeldung durch eine eigene ersetzt, verliert genau
//     die Information, die weiterhilft. Die Meldung kommt woertlich vom Server; nur wenn ueberhaupt
//     keine Antwort ankam, formuliert die Oberflaeche selbst.
//
// Zwei bewusste Entscheidungen:
//
//   * Kein Puls, kein Schimmern auf dem Skelett. Der Vertrag erlaubt Bewegung nur dort, wo sie etwas
//     bedeutet, 150 ms lang. Eine Dauerschleife waere beides nicht. Der Ladezustand ist ein
//     stiller Balken plus ein Satz fuer den Screenreader.
//   * Inline-Stile mit var(--token) statt Klassen. Diese Komponente muss identisch aussehen,
//     gleichgueltig welche Klassen sonst gelten. Es werden ausschliesslich Tokens benutzt.
import type { CSSProperties, ReactElement, ReactNode } from 'react'
import { ApiError } from '../api'

/**
 * Der Text im Fehlerzustand. Vom Server kommt er woertlich (die Routen antworten mit
 * `{error, message, field?}` und deutschem `message`); nur fuer den Fall, dass gar keine Antwort
 * ankam — Funkloch, abgebrochene Verbindung, DNS — formuliert die Oberflaeche selbst.
 */
export function fehlermeldung(fehler: unknown): string {
  if (fehler instanceof ApiError) return fehler.message
  return 'Die Verbindung zum Server ist unterbrochen. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.'
}

export type ZustandProps =
  | { art: 'laedt'; beschriftung: string; zeilen?: number }
  | { art: 'fehler'; fehler: unknown; onWiederholen: () => void }
  | { art: 'leer'; titel: string; hinweis: string; aktion?: ReactNode }

/** Genau die Hoehe einer Datenzeile. Aendert sich diese, aendert sich das Skelett mit. */
const ZEILENHOEHE = '2.75rem'
const STANDARD_SKELETTZEILEN = 5

const stilVersteckt: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
}

const stilBalken: CSSProperties = {
  display: 'block',
  height: '0.75rem',
  width: '100%',
  background: 'var(--rule)',
  borderRadius: '999px',
}

const stilSkelettzelle: CSSProperties = {
  height: ZEILENHOEHE,
  padding: '0 0.75rem',
  borderBottom: '1px solid var(--rule)',
  verticalAlign: 'middle',
}

const stilBlock: CSSProperties = {
  minHeight: `calc(${ZEILENHOEHE} * 3)`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '0.5rem',
  padding: '1.5rem 0.75rem',
}

const stilTitel: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--sans)',
  fontSize: '1.0625rem',
  fontWeight: 650,
  letterSpacing: '-0.01em',
  color: 'var(--ink)',
}

const stilHinweis: CSSProperties = {
  margin: 0,
  maxWidth: '52ch',
  color: 'var(--ink-muted)',
}

const stilFehlertext: CSSProperties = {
  margin: 0,
  maxWidth: '52ch',
  color: 'var(--alert)',
}

// Ein schlichter <button>, kein <Knopf>: Zustand haengt bewusst an keinem anderen Baustein, damit
// ein Fehlerzustand auch dann noch anzeigbar ist, wenn an Knopf gerade gearbeitet wird. Die Optik
// folgt denselben Tokens wie ein sekundaerer Knopf.
const stilKnopf: CSSProperties = {
  alignSelf: 'flex-start',
  font: 'inherit',
  padding: '0.375rem 0.875rem',
  color: 'var(--accent)',
  background: 'var(--paper)',
  border: '1px solid var(--accent)',
  borderRadius: '2px',
  cursor: 'pointer',
}

function VersteckterStatus({ text }: { text: string }): ReactElement {
  return (
    <span role="status" style={stilVersteckt}>
      {text}
    </span>
  )
}

function Fehlerinhalt({ fehler, onWiederholen }: { fehler: unknown; onWiederholen: () => void }): ReactElement {
  return (
    <div role="alert" style={stilBlock}>
      <p style={stilFehlertext}>{fehlermeldung(fehler)}</p>
      <button type="button" style={stilKnopf} onClick={onWiederholen}>
        Erneut versuchen
      </button>
    </div>
  )
}

function Leerinhalt({ titel, hinweis, aktion }: { titel: string; hinweis: string; aktion?: ReactNode }): ReactElement {
  return (
    <div style={stilBlock}>
      <p style={stilTitel}>{titel}</p>
      <p style={stilHinweis}>{hinweis}</p>
      {aktion}
    </div>
  )
}

/** Der Block-Zustand: fuer Bildschirme, deren Inhalt keine Tabelle ist (Dashboard, Profil, Auswahl). */
export function Zustand(props: ZustandProps): ReactElement {
  if (props.art === 'laedt') {
    const zeilen = props.zeilen ?? 3
    return (
      <div style={stilBlock} aria-busy="true">
        <VersteckterStatus text={props.beschriftung} />
        {Array.from({ length: zeilen }, (_, i) => (
          <span key={i} aria-hidden="true" style={stilBalken} />
        ))}
      </div>
    )
  }
  if (props.art === 'fehler') {
    return <Fehlerinhalt fehler={props.fehler} onWiederholen={props.onWiederholen} />
  }
  return <Leerinhalt titel={props.titel} hinweis={props.hinweis} aktion={props.aktion} />
}

/**
 * Derselbe Zustand als Tabellenzeile(n). Gehoert dorthin, wo die Datenzeilen entstehen — also neben
 * das `.map(...)`, das die Zeilen rendert. `spalten` ist die Anzahl der `<th>` im Kopf der Tabelle.
 */
export function TabellenZustand(props: ZustandProps & { spalten: number }): ReactElement {
  if (props.art === 'laedt') {
    const zeilen = props.zeilen ?? STANDARD_SKELETTZEILEN
    return (
      <>
        {Array.from({ length: zeilen }, (_, i) => (
          // Nur die erste Skelettzeile bleibt im Zugaenglichkeitsbaum und traegt den Statussatz.
          // Alle weiteren sind reine Platzhalter und wuerden sonst als leere Zeilen vorgelesen.
          <tr key={i} aria-hidden={i === 0 ? undefined : 'true'}>
            {Array.from({ length: props.spalten }, (_, j) => (
              <td key={j} style={stilSkelettzelle}>
                {i === 0 && j === 0 ? <VersteckterStatus text={props.beschriftung} /> : null}
                <span aria-hidden="true" style={stilBalken} />
              </td>
            ))}
          </tr>
        ))}
      </>
    )
  }
  return (
    <tr>
      <td colSpan={props.spalten} style={{ padding: 0 }}>
        {props.art === 'fehler' ? (
          <Fehlerinhalt fehler={props.fehler} onWiederholen={props.onWiederholen} />
        ) : (
          <Leerinhalt titel={props.titel} hinweis={props.hinweis} aktion={props.aktion} />
        )}
      </td>
    </tr>
  )
}
